// VendorCreateInputs.tsx
import { FormSection } from "@/components/common/inputs/FormSection";
import { ResourceName } from "@/types";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import { Box, InputAdornment, Stack, Typography } from "@mui/material";
import { TextInput, BooleanInput, NumberInput, DateInput } from "react-admin";
import { formatPhoneNumber } from "@/utils/validators/inPlaceValidators";
import { useWatch } from "react-hook-form";

const getVendorInputProps = (key: string, textReplace = {}) => getInputProps(key, textReplace, ResourceName.VENDORS);

export const VendorCreateInputs = () => {
  // watched values for conditional UI
  const isTpa = useWatch({ name: "is_tpa" });
  const isCarrier = useWatch({ name: "is_sl_carrier" });
  const hasChildrenCo = useWatch({ name: "has_children_co" });

  return (
    <Box p={2}>
      {/* Core vendor identification */}
      <TextInput {...getVendorInputProps("company_name")} required fullWidth size="small" />

      {/* Vendor type flags */}
      <FormSection title="Vendor Type">
        <BooleanInput {...getVendorInputProps("is_sl_carrier")} size="small" />
        <BooleanInput {...getVendorInputProps("is_rx_vendor")} size="small" />
        <BooleanInput {...getVendorInputProps("is_tpa")} size="small" />
        {isTpa && <BooleanInput {...getVendorInputProps("tpa_is_buca")} size="small" />}
        <BooleanInput {...getVendorInputProps("admin_pp")} size="small" />
      </FormSection>

      {/* Company relationships */}
      <FormSection title="Company Relationships">
        <BooleanInput {...getVendorInputProps("has_children_co")} size="small" />
        {hasChildrenCo && <TextInput {...getVendorInputProps("provider_parent_co")} fullWidth size="small" />}
      </FormSection>

      {/* External system identifiers */}
      <FormSection title="External Identifiers">
        <Stack direction="row" spacing={1}>
          <NumberInput {...getVendorInputProps("org_id")} size="small" />
          <NumberInput {...getVendorInputProps("license_number")} size="small" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <NumberInput {...getVendorInputProps("fein")} size="small" />
          <NumberInput {...getVendorInputProps("naic_code")} size="small" />
        </Stack>
      </FormSection>

      {/* Company metadata */}
      <FormSection title="Company Information">
        <TextInput {...getVendorInputProps("group_name")} fullWidth size="small" />
        <TextInput {...getVendorInputProps("website")} fullWidth size="small" />
        <TextInput
          {...getVendorInputProps("licensed_states")}
          fullWidth
          size="small"
          helperText="Comma-separated list of state abbreviations"
        />
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("category")} fullWidth size="small" />
          <TextInput {...getVendorInputProps("company_structure")} fullWidth size="small" />
        </Stack>
      </FormSection>

      {/* Carrier Ratings (only shown for stop-loss carriers) */}
      {isCarrier && (
        <FormSection title="Carrier Ratings">
          <Stack direction="row" spacing={1}>
            <TextInput {...getVendorInputProps("financial_strength_rating")} fullWidth size="small" />
            <TextInput {...getVendorInputProps("long_term_issuer_credit_rating")} fullWidth size="small" />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextInput {...getVendorInputProps("outlook")} fullWidth size="small" />
            <DateInput {...getVendorInputProps("rating_effective_date")} size="small" />
          </Stack>
        </FormSection>
      )}

      {/* Primary Contact */}
      <FormSection title="Primary Contact">
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("primary_contact.name")} fullWidth size="small" />
          <TextInput {...getVendorInputProps("primary_contact.title")} fullWidth size="small" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("primary_contact.email")} type="email" fullWidth size="small" />
          <TextInput
            {...getVendorInputProps("primary_contact.phone")}
            type="tel"
            parse={formatPhoneNumber}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary">
                    +1
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          <TextInput {...getVendorInputProps("primary_contact.phone_ext")} size="small" />
        </Stack>
      </FormSection>

      {/* Claims Contact */}
      <FormSection title="Claims Contact">
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("claims_contact.name")} fullWidth size="small" />
          <TextInput {...getVendorInputProps("claims_contact.title")} fullWidth size="small" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("claims_contact.email")} type="email" fullWidth size="small" />
          <TextInput
            {...getVendorInputProps("claims_contact.phone")}
            type="tel"
            parse={formatPhoneNumber}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary">
                    +1
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          <TextInput {...getVendorInputProps("claims_contact.phone_ext")} size="small" />
        </Stack>
      </FormSection>

      {/* Address */}
      <FormSection title="Address">
        <TextInput {...getVendorInputProps("address.street")} fullWidth size="small" />
        <Stack direction="row" spacing={1}>
          <TextInput {...getVendorInputProps("address.city")} fullWidth size="small" />
          <TextInput {...getVendorInputProps("address.state")} size="small" />
          <TextInput {...getVendorInputProps("address.zip")} size="small" />
        </Stack>
      </FormSection>
    </Box>
  );
};
